/**
 * Núcleo do tema novo (Eternità 2026).
 *
 * - Eventos padrão do Shopify: shopify:page:view e o elemento <s-view-event>.
 *   A biblioteca vem da CDN do Shopify por import dinâmico: se a CDN falhar,
 *   o resto do tema continua funcionando.
 * - Revelação no scroll (.e-reveal → .is-in).
 * - Polyfill mínimo de commandfor/command para <dialog>.
 * - Estado do cabeçalho ao rolar (data-scrolled).
 */

const whenReady = (callback) => {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback, { once: true });
  } else {
    callback();
  }
};

/* ---------- Eventos padrão ---------- */
const standardEvents = import('@shopify/standard-events')
  .then((library) => {
    if (!customElements.get('s-view-event')) {
      customElements.define('s-view-event', library.createViewEventElement());
    }
    return library;
  })
  .catch(() => null);

whenReady(async () => {
  const library = await standardEvents;
  if (!library) return;
  document.dispatchEvent(
    new library.PageViewEvent({
      page: {
        template: document.body.dataset.eTemplate ?? '',
        title: document.title,
        url: window.location.href,
      },
    }),
  );
});

/* ---------- Revelação no scroll ---------- */
function observeReveal() {
  const targets = document.querySelectorAll('.e-reveal:not(.is-in)');
  if (!targets.length) return;
  if (!('IntersectionObserver' in window)) {
    targets.forEach((element) => element.classList.add('is-in'));
    return;
  }
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-in');
        observer.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 },
  );
  targets.forEach((element) => observer.observe(element));
}

/* ---------- commandfor / command (Invoker Commands) ---------- */
function polyfillCommands() {
  if ('command' in HTMLButtonElement.prototype) return;
  document.addEventListener('click', (event) => {
    const button = event.target.closest('button[commandfor]');
    if (!button) return;
    const target = document.getElementById(button.getAttribute('commandfor'));
    if (!(target instanceof HTMLDialogElement)) return;
    const command = button.getAttribute('command');
    if (command === 'show-modal' && !target.open) target.showModal();
    if (command === 'close' && target.open) target.close();
  });
}

/* Clique no fundo fecha a gaveta onde closedby="any" ainda não existe. */
function closeDrawersOnBackdrop() {
  if ('closedBy' in HTMLDialogElement.prototype) return;
  document.addEventListener('click', (event) => {
    const dialog = event.target;
    if (dialog instanceof HTMLDialogElement && dialog.classList.contains('e-drawer')) dialog.close();
  });
}

/* ---------- Cabeçalho ao rolar ---------- */
function trackHeaderScroll() {
  let header = null;
  let ticking = false;
  const update = () => {
    header?.toggleAttribute('data-scrolled', window.scrollY > 8);
    ticking = false;
  };
  const bind = () => {
    header = document.querySelector('[data-e-header]');
    update();
  };
  window.addEventListener(
    'scroll',
    () => {
      if (ticking || !header) return;
      ticking = true;
      requestAnimationFrame(update);
    },
    { passive: true },
  );
  /* O editor de temas re-renderiza o cabeçalho: o elemento novo passa a receber o estado. */
  document.addEventListener('shopify:section:load', bind);
  bind();
}

polyfillCommands();
closeDrawersOnBackdrop();
whenReady(() => {
  observeReveal();
  trackHeaderScroll();
});
/* O editor de temas re-renderiza a seção: os elementos novos também precisam aparecer. */
document.addEventListener('shopify:section:load', observeReveal);
